import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Phone, Navigation } from 'lucide-react';
import * as crmBackend from '../services/crmBackendService';
import TechnicianMultiSelect from './TechnicianMultiSelect';
import EstimateItemsEditor from './EstimateItemsEditor';

// ERPNext host that serves /files/... attachments (customer signature images).
const ERP_HOST = 'https://erp.aaairrigationservice.com';

/**
 * Estimate Detail / Edit Dialog — extracted verbatim from Dashboard.jsx
 * (formerly inline lines 1301-1526) so both Dashboard.jsx and ContactDetail.jsx
 * (Phase 2B customer history timeline) can mount it. Markup and handlers are
 * unchanged; only state ownership moved from Dashboard-local useState to props
 * so the caller drives it.
 *
 * Props:
 *   open, onOpenChange           — dialog visibility (was estimateDetailOpen/setEstimateDetailOpen)
 *   estimate                     — the loaded estimate doc (was selectedEstimate)
 *   editForm, onEditFormChange   — form state + setter (was estimateEditForm/setEstimateEditForm)
 *   sending, onSend              — Send Estimate action (was sendingEstimate/handleSendEstimate)
 *   completing, onCompleteJob    — Complete Job + Invoice action (was completingJob/handleCompleteEstimateJob)
 *   saving, onSave               — Save Changes action (was estimateSaving/saveEstimate)
 *   getStatusColor               — status→className lookup (Dashboard-local function, passed through)
 *   onItemsChanged                — optional; called after any line-item add/edit/remove so the
 *                                   caller can re-fetch the estimate and refresh totals/status shown
 *                                   elsewhere (Dashboard/ContactDetail list rows, etc). Not required —
 *                                   the editor's own total display already reflects live state.
 */
export default function EstimateDetailDialog({
  open,
  onOpenChange,
  estimate: selectedEstimate,
  editForm: estimateEditForm,
  onEditFormChange: setEstimateEditForm,
  sending: sendingEstimate,
  onSend: handleSendEstimate,
  completing: completingJob,
  onCompleteJob: handleCompleteEstimateJob,
  saving: estimateSaving,
  onSave: saveEstimate,
  getStatusColor,
  onItemsChanged,
}) {
  const navigate = useNavigate();

  // Communications — same source ContactDetail.jsx/TechJobDetails.jsx use
  // (getContactHistory), filtered to just this estimate, so "was this
  // actually sent to the customer" is answered right where staff look after
  // clicking Send Estimate, not only on a separate Contact page.
  const [communications, setCommunications] = useState([]);
  const [commsLoading, setCommsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!open || !selectedEstimate?.party_name || !selectedEstimate?.name) {
        setCommunications([]);
        return;
      }
      setCommsLoading(true);
      try {
        const result = await crmBackend.getContactHistory(selectedEstimate.party_name, 'customer');
        const all = result?.data?.communications || [];
        const forThisEstimate = all.filter(
          (c) => c.reference_doctype === 'Quotation' && c.reference_name === selectedEstimate.name
        );
        if (!cancelled) setCommunications(forThisEstimate);
      } catch {
        if (!cancelled) setCommunications([]);
      } finally {
        if (!cancelled) setCommsLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
    // sendingEstimate included so a send-in-progress -> done transition
    // triggers an immediate refetch, without closing/reopening the dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedEstimate?.party_name, selectedEstimate?.name, sendingEstimate]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg lg:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{selectedEstimate?.name} — {selectedEstimate?.party_name || selectedEstimate?.customer_name}</span>
            {selectedEstimate?.custom_customer_phone && (
              <a href={`tel:${selectedEstimate.custom_customer_phone}`}
                className="flex items-center gap-1 text-sm font-normal text-green-700 hover:text-green-900 ml-4">
                <Phone className="w-4 h-4" />{selectedEstimate.custom_customer_phone}
              </a>
            )}
          </DialogTitle>
        </DialogHeader>
        {selectedEstimate && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <p className="text-gray-500">Total</p>
                <p className="font-semibold">{crmBackend.formatCurrency(selectedEstimate.grand_total)}</p>
              </div>
              <div>
                <p className="text-gray-500">Status</p>
                <Badge className={selectedEstimate.custom_pipeline_status
                  ? crmBackend.pipelineStatusColor(selectedEstimate.custom_pipeline_status)
                  : getStatusColor(selectedEstimate.custom_approval_status || selectedEstimate.status)}>
                  {selectedEstimate.custom_pipeline_status || selectedEstimate.custom_approval_status || selectedEstimate.status || 'Draft'}
                </Badge>
              </div>
              {selectedEstimate.custom_service_address && (
                <div className="col-span-2">
                  <div className="flex items-center justify-between">
                    <p className="text-gray-500">Service Address</p>
                    <a
                      href={`https://waze.com/ul?q=${encodeURIComponent(selectedEstimate.custom_service_address)}&navigate=yes`}
                      target="_blank" rel="noreferrer"
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Navigation className="w-3 h-3" /> Waze
                    </a>
                  </div>
                  <p className="font-medium whitespace-pre-line">{selectedEstimate.custom_service_address}</p>
                </div>
              )}
              {selectedEstimate.custom_customer_email && (
                <div className="col-span-2">
                  <p className="text-gray-500">Email</p>
                  <p className="font-medium">{selectedEstimate.custom_customer_email}</p>
                </div>
              )}
              {(selectedEstimate.custom_service_city || selectedEstimate.custom_service_state || selectedEstimate.custom_service_zip) && (
                <div className="col-span-2">
                  <p className="text-gray-500">City / State / ZIP</p>
                  <p className="font-medium">
                    {[selectedEstimate.custom_service_city, selectedEstimate.custom_service_state, selectedEstimate.custom_service_zip]
                      .filter(Boolean).join(', ')}
                  </p>
                </div>
              )}
              {selectedEstimate.custom_service_description && (
                <div className="col-span-2">
                  <p className="text-gray-500">Description</p>
                  <p className="font-medium">{selectedEstimate.custom_service_description}</p>
                </div>
              )}
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-gray-500">Line Items</p>
                  <Button
                    size="sm" variant="link"
                    className="h-auto p-0 text-xs text-blue-600 hover:text-blue-800"
                    onClick={() => { onOpenChange(false); navigate(`/tech/job/${selectedEstimate.name}/add-items`); }}
                  >
                    Open full page (photos)
                  </Button>
                </div>
                {/* Full add/remove/edit item management, no restrictions beyond
                    ERP's own approval-status/submitted-doc lock — see
                    EstimateItemsEditor.jsx. Reuses the same immediate-save,
                    verify, retry mechanism (lib/itemSync.js) the tech app's
                    Add Items page uses. */}
                <EstimateItemsEditor
                  jobId={selectedEstimate.name}
                  locked={
                    selectedEstimate.docstatus === 1
                    || selectedEstimate.custom_signature_captured === 1
                    || selectedEstimate.custom_approval_status === 'Approved'
                  }
                  onChanged={onItemsChanged}
                />
              </div>
            </div>
            <div>
              <p className="text-gray-500 text-sm mb-1">Communications</p>
              {commsLoading && <p className="text-xs text-gray-400">Loading…</p>}
              {!commsLoading && communications.length === 0 && (
                <p className="text-xs text-gray-400">Not sent to the customer yet.</p>
              )}
              {!commsLoading && communications.map((c) => (
                <p key={c.name} className="text-xs text-gray-600">
                  ✉️ Sent {c.communication_date
                    ? new Date(c.communication_date).toLocaleString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
                      })
                    : ''}
                </p>
              ))}
            </div>
            <hr />
            <div className="space-y-3">
              <div>
                <Label>Appointment Status</Label>
                <select
                  className="w-full mt-1 border rounded-md px-3 py-2 text-sm"
                  value={estimateEditForm.custom_appointment_status}
                  onChange={(e) => setEstimateEditForm((f) => ({ ...f, custom_appointment_status: e.target.value }))}
                >
                  {crmBackend.APPOINTMENT_STATUSES.map(s => (
                    <option key={s} value={s}>{s || '— Not set —'}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Job Status (technician progress)</Label>
                <select
                  className="w-full mt-1 border rounded-md px-3 py-2 text-sm"
                  value={estimateEditForm.custom_job_status}
                  onChange={(e) => setEstimateEditForm((f) => ({ ...f, custom_job_status: e.target.value }))}
                >
                  {crmBackend.JOB_STATUSES.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <p className="mt-1">
                  <Badge className={crmBackend.getJobStatusColor(estimateEditForm.custom_job_status)}>
                    {estimateEditForm.custom_job_status}
                  </Badge>
                </p>
              </div>
              <div>
                <Label>Scheduled Date</Label>
                <Input
                  type="date"
                  value={estimateEditForm.custom_scheduled_date}
                  onChange={(e) => setEstimateEditForm((f) => ({ ...f, custom_scheduled_date: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Time Window</Label>
                <div className="grid grid-cols-3 gap-1 mt-1">
                  {crmBackend.timeWindowOptions(estimateEditForm.custom_scheduled_time).map(w => (
                    <button key={w} type="button"
                      onClick={() => setEstimateEditForm(f => ({ ...f, custom_scheduled_time: w }))}
                      className={`h-8 rounded text-xs font-medium border transition-colors ${
                        estimateEditForm.custom_scheduled_time === w
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                      }`}>
                      {w}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <TechnicianMultiSelect
                  value={estimateEditForm.custom_technician}
                  onChange={(val) => setEstimateEditForm((f) => ({ ...f, custom_technician: val }))}
                />
              </div>
              <div>
                <Label>Customer Signature</Label>
                {/* Show the real signature image, not a "captured" placeholder.
                    custom_signature_image is an ERPNext Attach Image field, so it
                    holds a site-relative /files/... URL — resolve it against the ERP
                    host. Falls back to the old text only when there genuinely is no
                    image on the record. */}
                {selectedEstimate?.custom_signature_image ? (
                  <div className="mt-1 border rounded-md p-2 bg-white flex items-center justify-center" style={{ minHeight: 72 }}>
                    <img
                      src={
                        selectedEstimate.custom_signature_image.startsWith('/')
                          ? `${ERP_HOST}${selectedEstimate.custom_signature_image}`
                          : selectedEstimate.custom_signature_image
                      }
                      alt="Customer signature"
                      className="max-h-16 object-contain"
                    />
                  </div>
                ) : (
                  <div className={`mt-1 border rounded-md p-2 text-center text-sm font-medium ${
                    selectedEstimate?.custom_signature_captured
                      ? 'bg-gray-50 text-green-700'
                      : 'bg-gray-50 text-gray-500'
                  }`}>
                    {selectedEstimate?.custom_signature_captured
                      ? '✓ Signed by customer (image unavailable)'
                      : 'Not yet signed — customer signs on their Estimate in the portal'}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        <DialogFooter className="sticky bottom-0 z-10 -mx-6 -mb-6 flex-col gap-2 border-t bg-background px-6 py-3 sm:flex-row sm:justify-end">
          {/* Manual-only actions — each is its own deliberate press, never a
              side effect of Save Changes. */}
          {selectedEstimate && selectedEstimate.custom_approval_status !== 'Approved' && (
            <Button
              variant="outline"
              onClick={handleSendEstimate}
              disabled={sendingEstimate}
              className="border-amber-600 text-amber-700 hover:bg-amber-50"
            >
              {sendingEstimate
                ? 'Sending…'
                : selectedEstimate.custom_approval_status === 'Sent for Approval'
                  ? 'Re-send Estimate'
                  : 'Send Estimate'}
            </Button>
          )}
          {selectedEstimate && estimateEditForm.custom_job_status !== 'Completed'
            && estimateEditForm.custom_job_status !== 'Cancelled' && (
            <Button
              variant="outline"
              onClick={handleCompleteEstimateJob}
              disabled={completingJob}
              className="border-green-700 text-green-700 hover:bg-green-50"
            >
              {completingJob ? 'Completing…' : 'Complete Job + Invoice'}
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={saveEstimate} disabled={estimateSaving} className="bg-blue-600 hover:bg-blue-700">
            {estimateSaving ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
